import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Step, Typography, Stepper, StepLabel, Grid, Divider } from '@material-ui/core'
import useRouter from 'utils/useRouter'
import { CustomButton, ModalConfirmacao, SecaoGenerica } from 'components'
import { useCadaverService } from 'hooks/useCadaverService'
import { useAuth } from 'hooks/useAuth'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

import { FormLocalDeColeta } from 'components/FormLocalDeColeta/'
import { FormDescricao } from './FormDescricao'
import { FormCadeiaDeCustodia } from './FormCadeiaDeCustodia'
import { FormFotos } from './FormFotos'
import useStyles from './Form.styles'

const getSteps = () => ['Descrição introdutória', 'Local de coleta', 'Cadeia de Custódia', 'Arquivos']

export const Form = (props) => {
  const { cadaver, idCaso, setProcessando, processando, editar, onClickExcluir } = props
  const classes = useStyles()
  const router = useRouter()
  const { currentUser } = useAuth()
  const cadaverService = useCadaverService()

  // Estados locais usando useState nativo do React
  const [formState, setFormStateState] = useState({
    values: {
      nic: '',
      necroterio: null,
      causaJuridicaMortePreliminar: '',
      descricaoGeral: '',
      endOrig: null,
      dataCol: null,
      dataTrans: null,
      usuCol: null,
      usuTrans: null,
      usuarioColetaInput: '',
      usuarioTransporteInput: '',
      arquivos: [],
    },
    errors: {},
    touched: {},
  })

  const [processandoValidacao, setProcessandoValidacao] = useState(false)
  const [errorNicExistente, setErrorNicExistente] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [disableNextButton, setDisableNextButton] = useState(false)
  const [open, setOpen] = useState(false)
  const steps = getSteps()

  // API Emulada para manter compatibilidade com componentes filhos
  const setFormState = useCallback((updateFnOrObject) => {
    setFormStateState((prev) => {
      const result = typeof updateFnOrObject === 'function' ? updateFnOrObject(prev) : updateFnOrObject
      return {
        ...prev,
        ...result,
        values: {
          ...prev.values,
          ...(result.values || {}),
        },
        errors: {
          ...prev.errors,
          ...(result.errors || {}),
        },
      }
    })
  }, [])

  const handleChange = useCallback((event) => {
    const { name, value } = event.target
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
    }))
  }, [])

  const handleChangeNewValueByNameProperty = useCallback((event, newValue, name) => {
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: newValue,
      },
    }))
  }, [])

  const hasError = useCallback((name) => {
    return Boolean(formState.errors[name])
  }, [formState.errors])

  const hasErrorWithoutTouch = useCallback((name) => {
    return Boolean(formState.errors[name])
  }, [formState.errors])

  const cadaverForm = {
    formState,
    setFormState,
    handleChange,
    handleChangeNewValueByNameProperty,
    hasError,
    hasErrorWithoutTouch,
  }

  // Validação Geral de Formulário reativa
  useEffect(() => {
    const tempErrors = {}

    // Validação da Descrição (Etapa 0)
    if (!formState.values.nic || !formState.values.nic.toString().trim()) {
      tempErrors.nic = ['NIC é obrigatório']
    }
    if (!formState.values.necroterio) {
      tempErrors.necroterio = ['Necrotério é obrigatório']
    }
    if (!formState.values.causaJuridicaMortePreliminar) {
      tempErrors.causaJuridicaMortePreliminar = ['Causa jurídica preliminar é obrigatória']
    }
    if (!formState.values.descricaoGeral || !formState.values.descricaoGeral.toString().trim()) {
      tempErrors.descricaoGeral = ['Descrição geral é obrigatória']
    }

    // Validação do Local de Coleta (Etapa 1)
    if (!formState.values.endOrig) {
      tempErrors.endOrig = ['Local de coleta é obrigatório']
    }

    // Validação da Cadeia de Custódia (Etapa 2)
    if (!formState.values.usuCol) {
      tempErrors.usuCol = ['Responsável pela coleta é obrigatório']
    }
    if (!formState.values.usuTrans) {
      tempErrors.usuTrans = ['Responsável pelo transporte é obrigatório']
    }

    setFormStateState((prev) => {
      const hasChanged = JSON.stringify(prev.errors) !== JSON.stringify(tempErrors)
      if (hasChanged) {
        return {
          ...prev,
          errors: tempErrors,
        }
      }
      return prev
    })
  }, [
    formState.values.nic,
    formState.values.necroterio,
    formState.values.causaJuridicaMortePreliminar,
    formState.values.descricaoGeral,
    formState.values.endOrig,
    formState.values.usuCol,
    formState.values.usuTrans,
  ])

  useEffect(() => {
    setErrorNicExistente(false)
  }, [formState.values.nic])

  useEffect(() => {
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        arquivos: [],
      },
    }))
  }, [])

  useEffect(() => {
    if (cadaver.id > 0) {
      setFormStateState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          ...cadaver,
          arquivos: cadaver.arqs ? cadaver.arqs.map((arquivo) => arquivo.id) : [],
          endOrig: cadaver.endOrig,
          necroterio: cadaver.necroterio,
          dataCol: cadaver.dataColeta,
          dataTrans: cadaver.dataTransporte,
          usuCol: cadaver.usuCol,
          usuTrans: cadaver.usuTrans,
        },
      }))
    }
  }, [cadaver.id])

  const getStepTitle = () => {
    switch (activeStep) {
      case 0:
        return 'DESCRIÇÃO INTRODUTÓRIA'
      case 1:
        return 'LOCAL DE COLETA'
      case 2:
        return 'CADEIA DE CUSTÓDIA'
      case 3:
        return 'ARQUIVOS RELACIONADOS'
      default:
        return ''
    }
  }

  const getFormContent = () => {
    switch (activeStep) {
      case 0:
        return <FormDescricao cadaverForm={cadaverForm} cadaver={cadaver} errorNicExistente={errorNicExistente} />
      case 1:
        return formState.values && <FormLocalDeColeta form={cadaverForm} idCaso={idCaso || cadaver.id_caso} setDisableNextButton={setDisableNextButton} />
      case 2:
        return <FormCadeiaDeCustodia cadaverForm={cadaverForm} editar={cadaver.id} />
      case 3:
        return <FormFotos cadaverForm={cadaverForm} processando={processando} setProcessando={setProcessando} />
      default:
        return null
    }
  }

  const formError = () => {
    let disabled = false
    if (
      (activeStep === 0 && (hasError('nic') || !formState.values.nic)) ||
      hasError('necroterio') ||
      !formState.values.necroterio ||
      hasError('causaJuridicaMortePreliminar') ||
      !formState.values.causaJuridicaMortePreliminar ||
      errorNicExistente
    ) {
      disabled = true
    }

    if (activeStep === 1 && (hasError('endOrig') || !formState.values.endOrig)) {
      disabled = true
    }

    if (activeStep === 2 && (hasError('usuCol') || !formState.values.usuCol)) {
      disabled = true
    }

    if (activeStep === 2 && (hasError('usuTrans') || !formState.values.usuTrans)) {
      disabled = true
    }

    return disabled
  }

  const handleBack = () => {
    setActiveStep(activeStep - 1)
  }

  const handleNext = async () => {
    if (activeStep === 0) {
      const listagem = await cadaverService.listarEContarCadaveresPorNicAsync(formState.values.nic, setProcessandoValidacao)

      if (listagem.count > 0) {
        if (cadaver.id) {
          if (formState.values.nic === cadaver.nic) {
            setActiveStep(activeStep + 1)
            setErrorNicExistente(false)
          }
        } else {
          setActiveStep(activeStep)
          setErrorNicExistente(true)
        }
      } else {
        setActiveStep(activeStep + 1)
        setErrorNicExistente(false)
      }
    } else if (activeStep === 3) {
      setOpen(true)
    } else {
      setActiveStep(activeStep + 1)
    }
  }

  const handleConfirmar = async () => {
    let cadaverCadastrado = null
    if (!cadaver.id) {
      cadaverCadastrado = await cadaverService.cadastrarCadaver(formState.values, idCaso, setProcessando)
    } else {
      await cadaverService.editarCadaver(cadaver.id, idCaso, formState.values, setProcessando)
    }
    if (editar) {
      router.history.push(`/casos/caso/cadaver/detalhar/${cadaver.id}`)
    } else {
      router.history.push(`/casos/caso/detalhar/${cadaverCadastrado?.id_caso || idCaso}`)
    }
  }

  const getButtonConfirmLabel = () => {
    if (cadaver.id) return 'Editar cadáver'
    return 'Cadastrar cadáver'
  }

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Grid container className={classes.contentSplitter}>
        <Grid item xs={12} className={classes.downContainer}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <Typography component="h5" variant="h6" gutterBottom>
                {getStepTitle()}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              {getFormContent()}
            </Grid>
            {!disableNextButton && (
              <>
                <Grid item xs={12}>
                  <Divider style={{ margin: '24px 0 16px 0' }} />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.botoesFormulario}>
                    <div className={classes.botaoContainer}>
                      {activeStep === 0 ? (
                        <CustomButton
                          variant="outlined"
                          colorType="primary"
                          onClick={() => router.history.goBack()}
                          startIcon={<ArrowBackIcon />}
                          disabled={processando}
                        >
                          Voltar
                        </CustomButton>
                      ) : (
                        <CustomButton
                          variant="contained"
                          colorType="primary"
                          onClick={handleBack}
                          startIcon={<ArrowBackIcon />}
                          disabled={processando}
                        >
                          Etapa Anterior
                        </CustomButton>
                      )}
                    </div>
                    <div className={classes.botoesDireita}>
                      {editar && currentUser?.cargo?.id === 1 && (
                        <div className={classes.botaoContainer}>
                          <CustomButton
                            variant="contained"
                            colorType="error"
                            startIcon={<DeleteIcon />}
                            onClick={onClickExcluir}
                            disabled={processando}
                          >
                            Excluir
                          </CustomButton>
                        </div>
                      )}
                      <div className={classes.botaoContainer}>
                        <CustomButton
                          colorType="secondary"
                          variant="contained"
                          disabled={formError() || processando}
                          isLoading={processandoValidacao || processando}
                          onClick={handleNext}
                          endIcon={activeStep === steps.length - 1 ? <SaveIcon /> : <ArrowForwardIcon />}
                        >
                          {activeStep === steps.length - 1 ? getButtonConfirmLabel() : 'Próxima Etapa'}
                        </CustomButton>
                      </div>
                    </div>
                  </div>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* Modal de Confirmação Unificado */}
      <ModalConfirmacao
        aberto={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirmar}
        processando={processando}
        titulo="CONFIRMAÇÃO DOS DADOS"
        confirmarLabel={getButtonConfirmLabel()}
        cancelarLabel="Cancelar"
        maxWidth="md"
      >
        <Grid container spacing={3}>


          <Grid item md={6} sm={6} xs={12}>
            <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>NIC</Typography>
            <Typography>{formState.values.nic || 'Não informado'}</Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Necrotério</Typography>
            <Typography>
              {formState.values.necroterio
                ? `${formState.values.necroterio.nome} / ${formState.values.necroterio.divisao?.sigla} / ${formState.values.necroterio.divisao?.regional?.sigla}`
                : 'Não informado'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Possível causa jurídica da morte</Typography>
            <Typography>{formState.values.causaJuridicaMortePreliminar || 'Não informado'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Descrição geral</Typography>
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{formState.values.descricaoGeral || 'Não informado'}</Typography>
          </Grid>




          <SecaoGenerica titulo="Dados da Coleta" spacing={2}>
            <Grid item xs={12}>
              <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Local de Coleta</Typography>
              <Typography>
                {formState.values.endOrig
                  ? `${formState.values.endOrig.rua || ''}, ${formState.values.endOrig.numero || ''} - ${formState.values.endOrig.bairro || ''} - ${formState.values.endOrig.cidade || ''}/${formState.values.endOrig.estado || ''}`
                  : 'Não informado'}
              </Typography>
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Responsável pela coleta</Typography>
              <Typography>
                {cadaver?.usuCol?.nome?.toUpperCase() === 'OUTRO'
                  ? `${cadaver?.usuarioColetaInput?.toUpperCase() || 'NÃO INFORMADO'}`
                  : formState.values.usuCol?.nome?.toUpperCase() || 'NÃO INFORMADO'}
              </Typography>
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Typography style={{ fontSize: '13px', color: '#777', fontWeight: 'bold' }}>Responsável pelo transporte</Typography>
              <Typography>
                {cadaver?.usuTrans?.nome?.toUpperCase() === 'OUTRO'
                  ? `${cadaver?.usuarioTransporteInput?.toUpperCase() || 'NÃO INFORMADO'}`
                  : formState.values.usuTrans?.nome?.toUpperCase() || 'NÃO INFORMADO'}
              </Typography>
            </Grid>
          </SecaoGenerica>

        </Grid>
      </ModalConfirmacao>
    </div>
  )
}

Form.propTypes = {
  cadaver: PropTypes.object.isRequired,
  idCaso: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setProcessando: PropTypes.func.isRequired,
  processando: PropTypes.bool.isRequired,
  editar: PropTypes.bool,
}
